// Taille d'un graphique — lot A du CDC du 09/10 (§ 3), Marc le 11/10 : « par utilisateur, par
// paliers » (la poignée libre viendra en V2 s'il y tient).
//
// POURQUOI DES PALIERS. Une poignée glissée donne des grilles irrégulières, un état au pixel à
// stocker, et rien au clavier. Trois largeurs × deux hauteurs couvrent « je veux voir ce
// graphique en grand » ; le plein écran (#682) couvre « tout de suite, très grand ».
//
// Le bouton vit dans la rangée d'« Agrandir » et de la bascule Graphique | Tableau, avec le même
// habillage. Il n'apparaît qu'au survol de la carte, comme l'épingle, mais reste toujours
// atteignable au clavier (et toujours visible sur un écran tactile, qui n'a pas de survol). Le
// panneau est fait de vrais boutons radio : flèches dans un groupe, Tab entre les groupes, Échap
// referme et rend le focus au bouton.
import { useEffect, useId, useRef, useState } from 'react'
import { Scaling } from 'lucide-react'
import { TAILLE_DEFAUT, type Hauteur, type Largeur, type TailleGraphique } from './prefs'
import './TailleGraphique.css'

const LARGEURS: [Largeur, string][] = [[1, 'Normale'], [2, 'Large'], [3, 'Pleine largeur']]
const HAUTEURS: [Hauteur, string][] = [['s', 'Standard'], ['h', 'Haute']]

export function BoutonTaille({ titre, taille, onChange, largeurs = true, hauteurs = true,
                               masque = false }: {
  titre: string
  /** La taille mémorisée, `null` = taille par défaut. */
  taille: TailleGraphique | null
  onChange: (taille: TailleGraphique | null) => void
  /** `false` quand la carte a déjà toute la ligne (épinglée sur la synthèse). */
  largeurs?: boolean
  /** `false` quand le graphique a sa hauteur propre (pyramide, barres horizontales). */
  hauteurs?: boolean
  /** Plein écran ouvert : on n'y règle pas la taille, « Fermer » prend le relais. */
  masque?: boolean
}) {
  const [ouvert, setOuvert] = useState(false)
  const racine = useRef<HTMLSpanElement>(null)
  const bouton = useRef<HTMLButtonElement>(null)
  const id = useId()
  const t = taille ?? TAILLE_DEFAUT

  // Un clic ailleurs referme le panneau, sans voler le focus à ce qui l'a reçu.
  useEffect(() => {
    if (!ouvert) return
    const dehors = (e: PointerEvent) => {
      if (!racine.current?.contains(e.target as Node)) setOuvert(false)
    }
    document.addEventListener('pointerdown', dehors)
    return () => document.removeEventListener('pointerdown', dehors)
  }, [ouvert])
  const fermer = () => { setOuvert(false); bouton.current?.focus() }
  const nom = `Taille du graphique ${titre}`
  const visible = ouvert && !masque

  return (
    // Le clic ne remonte pas à la carte : elle ouvrirait « Comprendre ce chiffre ».
    // Le focus qui quitte le réglage le referme (Tab au-delà, « Agrandir » au clavier).
    <span ref={racine} className="obs2-taille" hidden={masque}
      onClick={e => e.stopPropagation()}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOuvert(false)
      }}
      onKeyDown={e => {
        if (e.key === 'Escape' && visible) { e.preventDefault(); e.stopPropagation(); fermer() }
      }}>
      <button ref={bouton} type="button" className={`obs2-taille-btn${taille ? ' active' : ''}`}
        aria-label={nom} aria-expanded={visible} aria-controls={visible ? id : undefined}
        title="Taille du graphique" onClick={() => setOuvert(o => !o)}>
        <Scaling size={12} aria-hidden="true" />
        Taille
      </button>
      {visible && (
        // tabIndex -1 : un clic dans le panneau (légende, marge) y garde le focus, sans quoi
        // la sortie de focus ci-dessus le refermerait.
        <div id={id} className="obs2-taille-panneau" role="group" aria-label={nom} tabIndex={-1}>
          {largeurs && (
            <fieldset>
              <legend>Largeur</legend>
              {LARGEURS.map(([l, libelle]) => (
                <label key={l}>
                  <input type="radio" name={`${id}-largeur`} checked={t.l === l}
                    onChange={() => onChange({ l, h: t.h })} />
                  {libelle}
                </label>
              ))}
            </fieldset>
          )}
          {hauteurs && (
            <fieldset>
              <legend>Hauteur</legend>
              {HAUTEURS.map(([h, libelle]) => (
                <label key={h}>
                  <input type="radio" name={`${id}-hauteur`} checked={t.h === h}
                    onChange={() => onChange({ l: t.l, h })} />
                  {libelle}
                </label>
              ))}
            </fieldset>
          )}
          <button type="button" className="obs2-taille-defaut" disabled={!taille}
            onClick={() => { onChange(null); fermer() }}>
            Taille par défaut
          </button>
        </div>
      )}
    </span>
  )
}
